/**
 * Workflow Output Viewer Component
 *
 * Displays final output and node outputs from completed workflows.
 * Supports viewing individual node outputs and downloading results.
 */

import React, { useState, useEffect } from 'react';
import { Download, ChevronDown, ChevronUp, FileText, AlertCircle, File, Image, ExternalLink, FileDown, Loader } from 'lucide-react';
import axios from 'axios';
import { downloadFile } from '../api/filesApi';
import { useAuth } from '../context/useAuth';

function detectContentType(text) {
  if (!text) return null;
  const t = text.trim();
  if (t.match(/^#{1,6}\s/) || t.includes('```') || t.match(/\[.*?\]\(.*?\)/) || t.match(/^[-*+]\s/m) || t.match(/\*\*/))
    return 'md';
  if (t.startsWith('{') || t.startsWith('[')) {
    try { JSON.parse(t); return 'json'; } catch (_) {}
  }
  if (t.includes(',') && t.split('\n').length > 1) {
    const lines = t.split('\n').filter(l => l.trim());
    if (lines.length > 1 && lines.filter(l => l.split(',').length > 1).length / lines.length > 0.7)
      return 'csv';
  }
  return 'txt';
}

export default function WorkflowOutputViewer({ workflowId }) {
  const { user } = useAuth();
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [showAllNodes, setShowAllNodes] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({}); // { nodeKey: true/false }
  const [globalPdfLoading, setGlobalPdfLoading] = useState(false);

  useEffect(() => {
    if (workflowId) {
      fetchOutput();
    }
  }, [workflowId, showAllNodes]);

  const fetchOutput = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `/api/v1/workflows/${workflowId}/output`,
        { params: { include_all_nodes: showAllNodes } }
      );
      setOutput(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `/api/v1/workflows/${workflowId}/output/download`,
        {
          params: { format },
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `workflow_${workflowId}_output.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download output');
    }
  };

  const handleGlobalPdfDownload = async () => {
    if (!output?.final_output) return;
    setGlobalPdfLoading(true);
    try {
      const response = await axios.post('/api/pdf/generate', {
        markdown_content: output.final_output,
        filename: `workflow_${workflowId}_output.pdf`,
        title: `Workflow Output`,
        workflow_id: workflowId,
      });
      if (response.data?.file_id) {
        await downloadFile(response.data.file_id, response.data.filename || `workflow_${workflowId}_output.pdf`);
      }
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGlobalPdfLoading(false);
    }
  };

  const toggleNodeExpand = (nodeKey) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeKey)) {
        next.delete(nodeKey);
      } else {
        next.add(nodeKey);
      }
      return next;
    });
  };

  // Download node output as raw .md file (client-side)
  const handleRawDownload = (node) => {
    const content = node.human_output || '';
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${node.display_name || node.node_key}.md`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Download node output as PDF via gateway (JWT-authenticated, with caching)
  const handlePdfDownload = async (node) => {
    if (!node.human_output) return;
    setPdfLoading(prev => ({ ...prev, [node.node_key]: true }));
    try {
      const response = await axios.post('/api/pdf/generate', {
        markdown_content: node.human_output,
        filename: `${node.display_name || node.node_key}.pdf`,
        title: node.display_name || node.node_key,
        workflow_id: workflowId,
        node_key: node.node_key,
      });
      // Download the generated PDF file
      if (response.data?.file_id) {
        await downloadFile(response.data.file_id, response.data.filename || `${node.display_name}.pdf`);
      }
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPdfLoading(prev => ({ ...prev, [node.node_key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading output...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle size={20} />
          <span className="font-medium">Error loading output</span>
        </div>
        <div className="text-sm text-red-600 mt-1">{error}</div>
      </div>
    );
  }

  if (!output || !output.final_output) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-yellow-700">
          <FileText size={20} />
          <span className="font-medium">No output available</span>
        </div>
        <div className="text-sm text-yellow-600 mt-1">
          This workflow hasn't produced output yet or hasn't completed.
        </div>
      </div>
    );
  }

  const nodesToDisplay = showAllNodes ? output.all_node_outputs : output.leaf_nodes;
  const contentType = detectContentType(output.final_output);

  return (
    <div className="space-y-4">
      {/* Header with download buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Workflow Output</h3>
        <div className="flex items-center gap-2">
          {/* Smart primary download based on content type */}
          {contentType === 'md' && (
            <button
              onClick={handleGlobalPdfDownload}
              disabled={globalPdfLoading}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition disabled:opacity-50"
              title="Convert markdown to PDF"
            >
              {globalPdfLoading ? <Loader size={16} className="animate-spin" /> : <FileDown size={16} />}
              PDF
            </button>
          )}
          {contentType === 'csv' && (
            <button
              onClick={() => handleDownload('csv')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition"
              title="Download as CSV"
            >
              <Download size={16} />
              CSV
            </button>
          )}
          <button
            onClick={() => handleDownload('txt')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition"
          >
            <Download size={16} />
            TXT
          </button>
          <button
            onClick={() => handleDownload('md')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition"
          >
            <Download size={16} />
            MD
          </button>
          <button
            onClick={() => handleDownload('json')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition"
          >
            <Download size={16} />
            JSON
          </button>
        </div>
      </div>

      {/* Final Output */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h4 className="font-medium text-gray-900 mb-2">Final Output</h4>
        <div className="prose prose-sm max-w-none">
          <div className="whitespace-pre-wrap text-gray-700 font-mono text-sm bg-gray-50 p-4 rounded">
            {output.final_output}
          </div>
        </div>
      </div>

      {/* Node Outputs */}
      {nodesToDisplay && nodesToDisplay.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">
              {showAllNodes ? 'All Node Outputs' : 'Leaf Node Outputs'} ({nodesToDisplay.length})
            </h4>
            <button
              onClick={() => setShowAllNodes(!showAllNodes)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showAllNodes ? 'Show leaf nodes only' : 'Show all nodes'}
            </button>
          </div>

          <div className="space-y-2">
            {nodesToDisplay.map((node) => {
              const isExpanded = expandedNodes.has(node.node_key);
              return (
                <div
                  key={node.node_key}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Node header */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition">
                    <button
                      onClick={() => toggleNodeExpand(node.node_key)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      <span className="font-medium text-gray-900">{node.display_name}</span>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {node.node_type}
                      </span>
                    </button>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {node.human_output && (
                        <>
                          <button
                            onClick={() => handlePdfDownload(node)}
                            disabled={pdfLoading[node.node_key]}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50"
                            title="Download as PDF"
                          >
                            {pdfLoading[node.node_key] ? <Loader size={12} className="animate-spin" /> : <FileDown size={12} />}
                            PDF
                          </button>
                          <button
                            onClick={() => handleRawDownload(node)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded transition"
                            title="Download as Markdown"
                          >
                            <FileDown size={12} />
                            MD
                          </button>
                        </>
                      )}
                      <span className="text-xs text-gray-500 ml-1">
                        Depth {node.depth_level} • Order {node.order_index}
                      </span>
                    </div>
                  </div>

                  {/* Node output (expandable) */}
                  {isExpanded && (
                    <div className="p-3 bg-white border-t border-gray-200">
                      {node.human_output && (
                        <div className="mb-3">
                          <div className="text-xs font-medium text-gray-500 mb-1">Human Output</div>
                          <div className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded">
                            {node.human_output}
                          </div>
                        </div>
                      )}
                      {node.task_context && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-1">Task Context</div>
                          <div className="text-sm text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded">
                            {node.task_context}
                          </div>
                        </div>
                      )}
                      {node.generated_files && node.generated_files.length > 0 && (
                        <div className="mt-3">
                          <div className="text-xs font-medium text-gray-500 mb-2">Generated Files</div>
                          <div className="space-y-2">
                            {node.generated_files.map((file, idx) => {
                              const isImage = file.mime_type?.startsWith('image/');
                              const isPdf = file.mime_type === 'application/pdf';
                              const FileIcon = isImage ? Image : File;
                              return (
                                <div
                                  key={file.file_id || idx}
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <FileIcon size={16} className="text-gray-500 shrink-0" />
                                    <span className="text-sm text-gray-700 truncate">{file.filename}</span>
                                    <span className="text-xs text-gray-400 shrink-0">{file.mime_type}</span>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0 ml-2">
                                    {(isPdf || isImage) && file.public_url && (
                                      <a
                                        href={file.public_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition"
                                      >
                                        <ExternalLink size={14} />
                                        Open
                                      </a>
                                    )}
                                    <button
                                      onClick={() => downloadFile(file.file_id, file.filename)}
                                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition"
                                    >
                                      <Download size={14} />
                                      Download
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
